import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../../components/ui/Navbar";
import Footer from "../../components/ui/Footer";
import { authApi } from "../../services/api";

const TIPS = [
  "Use at least 6 characters for your new password.",
  "Choose something unique that you don't use on other sites.",
  "You'll be redirected to log in once your password is updated.",
];

const inputClass = (hasError) =>
  `w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:outline-none focus:ring-2 ${
    hasError
      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
      : "border-slate-200 focus:border-[#0BBF64] focus:ring-[#0BBF64]/20"
  }`;

const FormField = ({ label, name, type = "text", value, onChange, error, placeholder }) => (
  <div>
    <label htmlFor={name} className="mb-1.5 block text-sm font-medium text-slate-700">
      {label}
      <span className="text-[#0BBF64]"> *</span>
    </label>
    <input
      id={name}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className={inputClass(!!error)}
      placeholder={placeholder}
    />
    {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
  </div>
);

const PasswordReset = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [token, setToken] = useState("");
  const [studentId, setStudentId] = useState("");

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    const studentIdParam = searchParams.get("studentId");

    if (!tokenParam || !studentIdParam) {
      setApiError("Invalid reset link. Token or student ID is missing.");
    } else {
      setToken(tokenParam);
      setStudentId(studentIdParam);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (apiError) setApiError("");
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token || !studentId) {
      setApiError(
        "Invalid reset link. Please request a new password reset."
      );
      return;
    }

    if (!validate()) return;

    setLoading(true);
    setApiError("");

    try {
      const response = await authApi.resetPassword({
        token,
        studentId,
        newPassword: formData.newPassword,
      });

      if (response.data && response.data.message === "successful") {
        navigate("/login", {
          state: {
            message:
              "Password reset successful! Please login with your new password.",
          },
        });
      } else {
        setApiError(
          response.data?.message ||
            "Password reset failed. Please try again."
        );
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Password reset failed. The link may have expired. Please request a new one.";
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const linkInvalid = !token || !studentId;

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900">
      <Navbar />

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10 lg:py-16">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 lg:items-stretch">
            {/* Marketing panel */}
            <section className="relative order-2 overflow-hidden rounded-2xl lg:order-1">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900" />
              <div
                aria-hidden
                className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:2.5rem_2.5rem]"
              />
              <div
                aria-hidden
                className="absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-[#0BBF64]/20 blur-3xl"
              />

              <div className="relative flex h-full flex-col justify-center p-8 sm:p-10 lg:p-12">
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">
                  New password
                </p>
                <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                  Set a fresh password
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">
                  Choose a strong password to secure your account and continue
                  working on your project documentation.
                </p>

                <ul className="mt-8 space-y-4">
                  {TIPS.map((text) => (
                    <li key={text} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-[#0BBF64]">
                        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                      <span className="text-sm leading-relaxed text-slate-200">
                        {text}
                      </span>
                    </li>
                  ))}
                </ul>

                {linkInvalid && (
                  <p className="mt-8 rounded-xl border border-amber-400/20 bg-amber-500/10 px-4 py-3 text-xs leading-relaxed text-amber-100">
                    This link appears invalid or expired.{" "}
                    <Link
                      to="/forgot-password"
                      className="font-semibold text-amber-200 hover:text-white"
                    >
                      Request a new reset link
                    </Link>
                  </p>
                )}
              </div>
            </section>

            {/* Form panel */}
            <section className="order-1 flex flex-col justify-center lg:order-2">
              <div className="mb-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#0BBF64]">
                  Reset password
                </p>
                <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                  Create a new password
                </h1>
              </div>

              <p className="mb-6 text-sm text-slate-600">
                Enter and confirm your new password below.
              </p>

              {apiError && (
                <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {apiError}
                  {linkInvalid && (
                    <p className="mt-2">
                      <Link
                        to="/forgot-password"
                        className="font-semibold text-red-800 underline hover:text-red-900"
                      >
                        Request a new reset link
                      </Link>
                    </p>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <FormField
                  label="New password"
                  name="newPassword"
                  type="password"
                  value={formData.newPassword}
                  onChange={handleChange}
                  error={errors.newPassword}
                  placeholder="At least 6 characters"
                />

                <FormField
                  label="Confirm password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={errors.confirmPassword}
                  placeholder="Repeat your new password"
                />

                <button
                  type="submit"
                  disabled={loading || linkInvalid}
                  className="inline-flex w-full items-center justify-center rounded-xl bg-[#0BBF64] px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/20 transition hover:bg-[#0aa459] hover:shadow-emerald-500/30 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Updating password…" : "Reset password"}
                </button>

                <p className="text-center text-sm text-slate-600">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-1.5 font-semibold text-[#0BBF64] hover:text-[#0aa459]"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Back to log in
                  </Link>
                </p>
              </form>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PasswordReset;
